// Dodawanie i edycja wpisów z planera /v2 — jedno miejsce, które wie, jakie pola
// przyjmuje API dla każdego typu. Modal renderuje pola, a stąd bierze szkic,
// walidację i gotowe żądanie. Pola bez odpowiednika w API tu nie istnieją.

import { ymd } from '@/composables/useV2Crud.js';
import { foodVersion } from '@/composables/usePlaner.js';
import { ingredientName, semiIngredientGrams } from '@/composables/useSemiProducts.js';
import { t } from '@/i18n/index.js';

const pad = (n) => String(n).padStart(2, '0');

const localYMD = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Backend oczekuje RFC 3339 z offsetem, bez milisekund.
const toIso = (date) => (date ? new Date(date).toISOString().replace(/\.\d{3}Z$/, '+00:00') : null);

// „HH:MM" + dzień → konkretny moment.
const combine = (day, hhmm) => {
    const [h, m] = String(hhmm ?? '').split(':').map(Number);
    const d = new Date(day);
    d.setHours(h || 0, m || 0, 0, 0);
    return d;
};

const hm = (value) => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const startOfDay = (value) => {
    const d = value instanceof Date ? new Date(value) : new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
};

const num = (value) => {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : null;
};

// Puste pole liczbowe zostawiamy puste, żeby nie zapisywać zer, których nie było.
const numStr = (value) => (value == null ? '' : String(value));

// Pola odżywcze składnika — jedna lista dla przeliczeń i sum.
const ING_FIELDS = ['calories', 'protein', 'fat', 'saturated_fat', 'carbs', 'sugar', 'fiber', 'salt', 'price'];

// Typy w kolejności z projektu: od tego, co dodaje się najczęściej.
export const ENTRY_TYPES = [
    {
        key: 'meal', icon: 'pi pi-apple',
        get label() { return t('entry.types.meal.label'); },
        get title() { return t('entry.types.meal.title'); },
        get editTitle() { return t('entry.types.meal.editTitle'); },
    },
    {
        key: 'sleep', icon: 'pi pi-moon',
        get label() { return t('entry.types.sleep.label'); },
        get title() { return t('entry.types.sleep.title'); },
        get editTitle() { return t('entry.types.sleep.editTitle'); },
    },
    {
        key: 'task', icon: 'pi pi-check',
        get label() { return t('entry.types.task.label'); },
        get title() { return t('entry.types.task.title'); },
        get editTitle() { return t('entry.types.task.editTitle'); },
    },
    {
        key: 'event', icon: 'pi pi-calendar',
        get label() { return t('entry.types.event.label'); },
        get title() { return t('entry.types.event.title'); },
        get editTitle() { return t('entry.types.event.editTitle'); },
    },
    {
        key: 'note', icon: 'pi pi-align-left',
        get label() { return t('entry.types.note.label'); },
        get title() { return t('entry.types.note.title'); },
        get editTitle() { return t('entry.types.note.editTitle'); },
    },
    {
        key: 'report', icon: 'pi pi-comment',
        get label() { return t('entry.types.report.label'); },
        get title() { return t('entry.types.report.title'); },
        get editTitle() { return t('entry.types.report.editTitle'); },
    },
    {
        key: 'workout', icon: 'pi pi-bolt',
        get label() { return t('entry.types.workout.label'); },
        get title() { return t('entry.types.workout.title'); },
        get editTitle() { return t('entry.types.workout.editTitle'); },
    },
];

// Ścieżki zasobów — edycja to ta sama ścieżka z uuid.
const ENDPOINTS = {
    meal: '/meal-entries',
    workout: '/workouts',
    sleep: '/sleep-entries',
    task: '/checklist-items',
    note: '/notes',
    report: '/day-reports',
    event: '/calendar-events',
};

// Koniec wydarzenia z godziną. Gdy wypada przed początkiem tego samego dnia
// („22:00 – 01:00"), chodzi o noc, więc przenosimy go na następny dzień.
const eventEnd = (draft) => {
    const start = combine(draft.day, draft.time);
    const end = combine(draft.endDay ?? draft.day, draft.endTime);
    if (end < start && localYMD(draft.endDay ?? draft.day) === localYMD(draft.day)) end.setDate(end.getDate() + 1);
    return end;
};

// „HH:MM" przesunięte o godzinę — domyślny koniec wydarzenia.
const plusHour = (hhmm) => {
    const [h, m] = String(hhmm ?? '').split(':').map(Number);
    return `${pad(((h || 0) + 1) % 24)}:${pad(m || 0)}`;
};

export function useV2Entry() {
    // Wspólny szkic dla wszystkich typów — modal pokazuje tylko pola swojego typu.
    const createDraft = (day, now = new Date()) => {
        // Posiłek na przyszły dzień to plan; dzisiejszy i miniony — zjedzony.
        const future = startOfDay(day) > startOfDay(now);

        return {
            day: new Date(day),
            time: `${pad(now.getHours())}:${pad(now.getMinutes())}`,

            // posiłek
            name: '',
            status: future ? 'planned' : 'eaten',
            calories: '', protein: '', carbs: '', fat: '',
            saturated_fat: '', sugar: '', fiber: '', salt: '', price: '',
            // Skład wpisu. `ingredientsMode: 'list'` = makra liczymy ze składu
            // i wysyłamy go do API; 'manual' = makra wpisane ręcznie, składu nie ruszamy.
            ingredients: [],
            ingredientsMode: 'manual',
            hadIngredients: false,
            mealUuid: null,

            // trening — jak posiłek: na przyszły dzień planujemy, wstecz zapisujemy fakt
            workoutType: null,
            workoutStatus: future ? 'planned' : 'done',
            durationH: '', durationMin: '30',
            activeCalories: '300',
            distanceKm: '',
            sourceWorkout: null,

            // sen — drzemki są dla wpisu snu tym, czym skład dla posiłku
            bedtime: '23:00', wakeUpAt: '07:00', quality: '', sleepNotes: '',
            naps: [],

            // zadanie
            text: '',
            checklistUuid: null,
            withTime: false,
            // „Do rozplanowania": zadanie bez dnia (i bez godziny)
            noDay: false,
            // zadanie cykliczne: none | daily | weekdays | weekly | monthly; dni tygodnia ISO 1–7
            repeat: 'none',
            repeatInterval: 1,
            repeatDays: [],
            repeatUntil: null,
            // przypomnienie push: minuty przed godziną (0 = o czasie), null = brak
            remindBefore: null,

            // notatka / wpis dnia (wydarzenie też: tytuł i opis)
            title: '',
            content: '',

            // wydarzenie — koniec domyślnie godzinę po początku, w tym samym dniu
            allDay: false,
            endDay: new Date(day),
            endTime: plusHour(`${pad(now.getHours())}:${pad(now.getMinutes())}`),
            location: '',
        };
    };

    // Snapshot składnika w kształcie, jaki przyjmuje API wpisu. Składnik-półprodukt
    // posiłku nie ma wagi, a wpis dnia jej wymaga — gramy liczymy z przepisu.
    const toIngredient = (source) => ({
        product_name: ingredientName(source),
        weight: num(source.weight) ?? (source.semi_product ? semiIngredientGrams(source) : 0),
        ...Object.fromEntries(ING_FIELDS.map(k => [k, num(source[k])])),
    });

    // Składnik zapisanego posiłku: `Ingredient.price` z API jest w złotówkach (bierze
    // się z ceny produktu za kilogram), a wpis dnia trzyma grosze — bez przeliczenia
    // posiłek za 7 zł zapisywał się jako 7 groszy. Pozostałe pola mają w obu
    // miejscach te same jednostki.
    const toMealIngredient = (source) => ({
        ...toIngredient(source),
        price: source.price === '' || source.price == null ? null : Math.round(Number(source.price) * 100),
    });

    // Składnik z produktu: makra produktu są per 100 g, cena w PLN za kg.
    const ingredientFromProduct = (product, weight) => {
        const w = num(weight) ?? 0;
        const factor = w / 100;
        return {
            product_name: product.name,
            weight: w,
            calories: Math.round((Number(product.calories) || 0) * factor),
            protein: Math.round((Number(product.protein) || 0) * factor),
            fat: Math.round((Number(product.fat) || 0) * factor),
            saturated_fat: Math.round((Number(product.saturated_fat) || 0) * factor),
            carbs: Math.round((Number(product.carbs) || 0) * factor),
            sugar: Math.round((Number(product.sugar) || 0) * factor),
            fiber: Math.round((Number(product.fiber) || 0) * factor),
            salt: Math.round((Number(product.salt) || 0) * factor),
            price: Math.round((Number(product.price) || 0) * w / 1000 * 100),
        };
    };

    // Zmiana wagi składnika skaluje jego wartości proporcjonalnie.
    const rescaleIngredient = (ingredient, weight) => {
        const w = num(weight) ?? 0;
        const from = ingredient.weight || 0;
        if (!from || !w) return { ...ingredient, weight: w };
        const factor = w / from;
        return {
            ...ingredient,
            weight: w,
            ...Object.fromEntries(ING_FIELDS.map(k => [
                k, ingredient[k] == null ? null : Math.round(ingredient[k] * factor),
            ])),
        };
    };

    // Sumy ze składu — wypełniają pola makr, żeby było widać, co się zapisze.
    const sumIngredients = (list = []) =>
        Object.fromEntries(ING_FIELDS.map(k => [k, list.reduce((a, i) => a + (i[k] ?? 0), 0)]));

    // Przepisuje sumy składu do pól szkicu (cena z groszy na złotówki).
    const withIngredients = (draft, list) => {
        const totals = sumIngredients(list);
        return {
            ...draft,
            ingredients: list,
            ingredientsMode: 'list',
            calories: numStr(totals.calories),
            protein: numStr(totals.protein),
            carbs: numStr(totals.carbs),
            fat: numStr(totals.fat),
            saturated_fat: numStr(totals.saturated_fat),
            sugar: numStr(totals.sugar),
            fiber: numStr(totals.fiber),
            salt: numStr(totals.salt),
            price: totals.price ? (totals.price / 100).toFixed(2) : '',
        };
    };

    // Szkic wypełniony istniejącym wpisem — do edycji.
    const draftFromEntry = (type, entry, now = new Date()) => {
        if (type === 'meal') {
            const at = entry.planned_at ? new Date(entry.planned_at) : new Date();
            const ingredients = (entry.ingredients ?? []).map(toIngredient);
            const draft = {
                ...createDraft(at, now),
                day: startOfDay(at),
                time: hm(at),
                name: entry.name ?? '',
                status: entry.status === 'eaten' ? 'eaten' : 'planned',
                calories: numStr(entry.calories),
                protein: numStr(entry.protein),
                carbs: numStr(entry.carbs),
                fat: numStr(entry.fat),
                saturated_fat: numStr(entry.saturated_fat),
                sugar: numStr(entry.sugar),
                fiber: numStr(entry.fiber),
                salt: numStr(entry.salt),
                price: entry.price == null ? '' : String((entry.price / 100).toFixed(2)),
                hadIngredients: ingredients.length > 0,
            };
            // Wpis ze składem edytujemy przez skład, ale pokazane makra zostają
            // te z wpisu — sumujemy dopiero, gdy skład faktycznie się zmieni.
            // Inaczej składnik bez zapisanych makr wyzerowałby wartości wpisu.
            return ingredients.length
                ? { ...draft, ingredients, ingredientsMode: 'list' }
                : draft;
        }

        if (type === 'workout') {
            const at = entry.performed_at ? new Date(entry.performed_at) : new Date();
            const seconds = entry.duration_seconds;
            return {
                ...createDraft(at, now),
                day: startOfDay(at),
                time: hm(at),
                workoutType: entry.type?.uuid ?? entry.type ?? null,
                workoutStatus: entry.status === 'planned' ? 'planned' : 'done',
                durationH: seconds == null ? '' : numStr(Math.floor(seconds / 3600)),
                durationMin: seconds == null ? '' : numStr(Math.round((seconds % 3600) / 60)),
                activeCalories: numStr(entry.active_calories),
                distanceKm: entry.distance_meters == null ? '' : String(entry.distance_meters / 1000),
            };
        }

        if (type === 'sleep') {
            const day = entry.date ? startOfDay(entry.date) : startOfDay(new Date());
            return {
                ...createDraft(day, now),
                day,
                // Puste godziny zostawiamy puste — wpis może być jeszcze niedokończony.
                bedtime: hm(entry.bedtime) ?? '',
                wakeUpAt: hm(entry.wake_up_at) ?? '',
                quality: numStr(entry.quality),
                sleepNotes: entry.notes ?? '',
                naps: (entry.naps ?? []).map(n => ({
                    uuid: n.uuid ?? null,
                    startTime: hm(n.start_at) ?? '',
                    endTime: hm(n.end_at) ?? '',
                    quality: numStr(n.quality),
                    notes: n.notes ?? '',
                })),
            };
        }

        if (type === 'task') {
            const day = entry.planned_for ? startOfDay(entry.planned_for) : startOfDay(new Date());
            return {
                ...createDraft(day, now),
                day,
                text: entry.text ?? '',
                withTime: !!entry.due_at,
                time: hm(entry.due_at) ?? `${pad(now.getHours())}:${pad(now.getMinutes())}`,
                checklistUuid: entry.checklist_uuid ?? null,
                // projekt zapisany w zadaniu — zostaje na liście wyboru, nawet gdy jest zakończony
                savedChecklistUuid: entry.checklist_uuid ?? null,
                noDay: !entry.planned_for,
                repeat: entry.recurrence?.freq ?? 'none',
                repeatInterval: entry.recurrence?.interval ?? 1,
                repeatDays: entry.recurrence?.days ?? [],
                repeatUntil: entry.recurrence?.until ? startOfDay(entry.recurrence.until) : null,
                remindBefore: entry.remind_before ?? null,
            };
        }

        if (type === 'event') {
            if (entry.all_day) {
                const [sy, sm, sd] = entry.start_date.split('-').map(Number);
                const [ey, em, ed] = entry.end_date.split('-').map(Number);
                const start = new Date(sy, sm - 1, sd);
                return {
                    ...createDraft(start, now),
                    day: start,
                    endDay: new Date(ey, em - 1, ed),
                    allDay: true,
                    title: entry.title ?? '',
                    content: entry.description ?? '',
                    location: entry.location ?? '',
                };
            }
            const start = new Date(entry.starts_at);
            const end = new Date(entry.ends_at);
            return {
                ...createDraft(start, now),
                day: startOfDay(start),
                time: hm(start),
                endDay: startOfDay(end),
                endTime: hm(end),
                allDay: false,
                title: entry.title ?? '',
                content: entry.description ?? '',
                location: entry.location ?? '',
            };
        }

        if (type === 'note') {
            return { ...createDraft(startOfDay(now), now), title: entry.title ?? '', content: entry.content ?? '' };
        }

        const at = entry.reported_at ? new Date(entry.reported_at) : new Date();
        return {
            ...createDraft(at, now),
            day: startOfDay(at),
            time: hm(at),
            content: entry.content ?? '',
        };
    };

    // Wypełnia szkic posiłku zapisanym posiłkiem z katalogu: nazwa i skład,
    // z którego liczą się makra.
    const applyMeal = (draft, meal) => {
        if (!meal) return { ...draft, mealUuid: null, ingredients: [], ingredientsMode: 'manual' };

        const ingredients = (meal.ingredients ?? []).map(toMealIngredient);
        return withIngredients(
            { ...draft, mealUuid: meal.uuid, name: meal.name ?? draft.name },
            ingredients,
        );
    };

    // Trening nie ma katalogu ani statusu w API, więc jego odpowiednikiem
    // „wyboru z listy" jest powtórzenie wcześniejszego treningu: rodzaj, czas,
    // kalorie i dystans. Data i godzina zostają te z formularza.
    const applyWorkout = (draft, workout) => {
        if (!workout) return { ...draft, sourceWorkout: null };
        const seconds = workout.duration_seconds ?? 0;
        return {
            ...draft,
            sourceWorkout: workout.uuid,
            workoutType: workout.type?.uuid ?? workout.type ?? draft.workoutType,
            durationH: numStr(Math.floor(seconds / 3600)),
            durationMin: numStr(Math.round((seconds % 3600) / 60)),
            activeCalories: numStr(workout.active_calories),
            distanceKm: workout.distance_meters == null ? '' : String(workout.distance_meters / 1000),
        };
    };

    const validate = (type, draft) => {
        const errors = [];
        const need = (cond, field, message) => { if (cond) errors.push({ field, message }); };

        if (type === 'meal') {
            need(!draft.name?.trim(), 'name', t('entry.validation.nameRequired'));
            need(!draft.time, 'time', t('entry.validation.timeRequired'));
        }
        if (type === 'workout') {
            need(!draft.workoutType, 'workoutType', t('entry.validation.workoutTypeRequired'));
            need(!draft.time, 'time', t('entry.validation.timeRequired'));
            // Czas i kalorie API wymaga tylko od treningu wykonanego —
            // planując, znamy zwykle sam rodzaj i godzinę.
            if (draft.workoutStatus === 'done') {
                const seconds = (num(draft.durationH) ?? 0) * 3600 + (num(draft.durationMin) ?? 0) * 60;
                need(seconds <= 0, 'duration', t('entry.validation.doneWorkoutDuration'));
                need(num(draft.activeCalories) == null, 'activeCalories', t('entry.validation.doneWorkoutCalories'));
            }
        }
        if (type === 'sleep') {
            // Zaśnięcie i pobudkę można dopisać później — wpis potrzebuje
            // przynajmniej jednej z tych godzin, żeby trafić na oś dnia.
            need(!draft.bedtime && !draft.wakeUpAt, 'bedtime', t('entry.validation.sleepTimeRequired'));
            const q = num(draft.quality);
            need(q != null && (q < 1 || q > 100), 'quality', t('entry.validation.qualityRange'));
            for (const nap of draft.naps ?? []) {
                if (!nap.startTime || !nap.endTime) {
                    need(true, 'naps', t('entry.validation.napTimes'));
                    break;
                }
            }
        }
        if (type === 'task') {
            need(!draft.text?.trim(), 'text', t('entry.validation.taskTextRequired'));
            need(draft.withTime && !draft.time, 'time', t('entry.validation.taskTimeRequired'));
            need(draft.repeat === 'weekly' && !(draft.repeatDays ?? []).length, 'repeat', t('entry.validation.weekdayRequired'));
            need(draft.repeat !== 'none' && draft.noDay, 'repeat', t('entry.validation.recurringNeedsDay'));
        }
        if (type === 'note') {
            need(!draft.content?.trim() && !draft.title?.trim(), 'content', t('entry.validation.noteContentRequired'));
        }
        if (type === 'event') {
            need(!draft.title?.trim(), 'title', t('entry.validation.eventTitleRequired'));
            if (draft.allDay) {
                need(startOfDay(draft.endDay) < startOfDay(draft.day), 'endDay', t('entry.validation.endBeforeStart'));
            } else {
                need(!draft.time, 'time', t('entry.validation.startTimeRequired'));
                need(!draft.endTime, 'endTime', t('entry.validation.endTimeRequired'));
                need(!!draft.time && !!draft.endTime && eventEnd(draft) < combine(draft.day, draft.time), 'endTime', t('entry.validation.endBeforeStart'));
            }
        }
        if (type === 'report') {
            need(!draft.content?.trim(), 'content', t('entry.validation.reportContentRequired'));
            need(!draft.time, 'time', t('entry.validation.timeRequired'));
        }
        return errors;
    };

    // Zwraca { method, url, body } — dokładnie te pola, które API przyjmuje.
    // `entry` obecne = edycja: PATCH na zasób o tym uuid.
    const buildRequest = (type, draft, entry = null) => {
        const at = () => toIso(combine(draft.day, draft.time));
        const uuid = entry?.uuid ?? entry?.id ?? null;
        const method = uuid ? 'patch' : 'post';
        const url = uuid ? `${ENDPOINTS[type]}/${uuid}` : ENDPOINTS[type];

        // Nowy posiłek przy nowym Jedzeniu (Planer v2 je pokazuje) idzie do niego jako wpis
        // z wartościami — pora dobierze się z godziny.
        if (type === 'meal' && !uuid && foodVersion.value === 'new') {
            const nutrition = Object.fromEntries(['calories', 'protein', 'carbs', 'fat', 'saturated_fat', 'sugar', 'fiber', 'salt'].map(k => [k, num(draft[k]) ?? 0]));
            return {
                method: 'post',
                url: '/food/entries',
                body: { date: ymd(draft.day), time: draft.time, status: draft.status, name: draft.name.trim(), nutrition, items: [] },
            };
        }

        if (type === 'meal') {
            const body = {
                name: draft.name.trim(),
                planned_at: at(),
                status: draft.status,
                calories: num(draft.calories),
                protein: num(draft.protein),
                carbs: num(draft.carbs),
                fat: num(draft.fat),
                saturated_fat: num(draft.saturated_fat),
                sugar: num(draft.sugar),
                fiber: num(draft.fiber),
                salt: num(draft.salt),
                // Cena w groszach — w formularzu podaje się złotówki.
                price: draft.price === '' || draft.price == null ? null : Math.round(Number(draft.price) * 100),
            };
            // Skład wysyłamy, gdy makra mają się z niego liczyć. Pusta lista przy
            // wpisie, który skład miał, to jawne polecenie usunięcia składu.
            // W trybie ręcznym pola `ingredients` nie ma — API nie rusza składu.
            if (draft.ingredientsMode === 'list' && (draft.ingredients?.length || draft.hadIngredients)) {
                body.ingredients = draft.ingredients ?? [];
            }
            return { method, url, body };
        }

        if (type === 'workout') {
            const seconds = (num(draft.durationH) ?? 0) * 3600 + (num(draft.durationMin) ?? 0) * 60;
            return {
                method, url,
                body: {
                    type: draft.workoutType,
                    performed_at: at(),
                    status: draft.workoutStatus,
                    // Zaplanowany trening może nie mieć jeszcze czasu ani kalorii.
                    duration_seconds: seconds > 0 ? seconds : null,
                    active_calories: num(draft.activeCalories),
                    distance_meters: draft.distanceKm === '' || draft.distanceKm == null
                        ? null
                        : Math.round(Number(draft.distanceKm) * 1000),
                },
            };
        }

        if (type === 'sleep') {
            // Noc należy do dnia pobudki (`draft.day`). Zaśnięcie po godzinie pobudki —
            // a bez pobudki: po południu — to wieczór dnia wcześniej.
            const wake = draft.wakeUpAt ? combine(draft.day, draft.wakeUpAt) : null;
            const bed = draft.bedtime ? combine(draft.day, draft.bedtime) : null;
            if (bed && (wake ? bed.getTime() >= wake.getTime() : bed.getHours() >= 12)) bed.setDate(bed.getDate() - 1);

            return {
                method, url,
                body: {
                    date: localYMD(draft.day),
                    bedtime: toIso(bed),
                    wake_up_at: toIso(wake),
                    quality: num(draft.quality),
                    notes: draft.sleepNotes?.trim() || null,
                    naps: (draft.naps ?? [])
                        .filter(n => n.startTime && n.endTime)
                        .map(n => {
                            const start = combine(draft.day, n.startTime);
                            const end = combine(draft.day, n.endTime);
                            // Drzemka przez północ (rzadkie, ale możliwe).
                            if (end.getTime() <= start.getTime()) end.setDate(end.getDate() + 1);
                            const nap = {
                                start_at: toIso(start),
                                end_at: toIso(end),
                                quality: num(n.quality),
                                notes: n.notes?.trim() || null,
                            };
                            if (n.uuid) nap.uuid = n.uuid;
                            return nap;
                        }),
                },
            };
        }

        if (type === 'task') {
            return {
                method, url,
                body: {
                    text: draft.text.trim(),
                    done: entry?.done ?? false,
                    planned_for: draft.noDay ? null : localYMD(draft.day),
                    due_at: draft.withTime && !draft.noDay ? at() : null,
                    // Projekt wysyłamy tylko przy nowym zadaniu albo zmianie — API przy każdej
                    // zmianie checklist_uuid zeruje sekcję zadania.
                    ...(!entry || (draft.checklistUuid || null) !== (entry.checklist_uuid ?? null)
                        ? { checklist_uuid: draft.checklistUuid || null }
                        : {}),
                    recurrence: draft.repeat && draft.repeat !== 'none' && !draft.noDay
                        ? {
                            freq: draft.repeat,
                            interval: Math.max(1, Number(draft.repeatInterval) || 1),
                            ...(draft.repeat === 'weekly' ? { days: [...draft.repeatDays].sort() } : {}),
                            ...(draft.repeatUntil ? { until: localYMD(draft.repeatUntil) } : {}),
                        }
                        : null,
                    remind_before: draft.withTime && !draft.noDay ? (draft.remindBefore ?? null) : null,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                },
            };
        }

        if (type === 'event') {
            const common = {
                title: draft.title.trim(),
                location: draft.location?.trim() || null,
                description: draft.content?.trim() || null,
            };
            return {
                method, url,
                body: draft.allDay
                    ? { ...common, all_day: true, start_date: localYMD(draft.day), end_date: localYMD(draft.endDay) }
                    : { ...common, all_day: false, starts_at: at(), ends_at: toIso(eventEnd(draft)) },
            };
        }

        if (type === 'note') {
            return {
                method, url,
                body: {
                    title: draft.title?.trim() || null,
                    content: draft.content?.trim() || null,
                },
            };
        }

        return {
            method, url,
            body: {
                reported_at: at(),
                content: draft.content.trim(),
            },
        };
    };

    return {
        ENTRY_TYPES, createDraft, draftFromEntry, applyMeal, applyWorkout, validate, buildRequest,
        ingredientFromProduct, rescaleIngredient, sumIngredients, withIngredients,
        localYMD, toIso, combine,
    };
}
